/**
 * Utilitário de validação de URL para prevenir vulnerabilidades de segurança
 * Especificamente projetado para bloquear data: URIs que podem causar DoS
 */

export class SecurityError extends Error {
  constructor(message) {
    super(message);
    this.name = 'SecurityError';
  }
}

/**
 * Valida se uma URL é segura para uso com Axios
 * Bloqueia data: URIs e outros protocolos não seguros
 *
 * @param {string} url - URL a ser validada
 * @throws {SecurityError} - Se a URL for considerada insegura
 */
export function validateSecureUrl(url) {
  if (!url || typeof url !== 'string') {
    throw new SecurityError('URL inválida: deve ser uma string não vazia');
  }

  // Bloquear data: URIs completamente por questões de segurança
  if (url.toLowerCase().startsWith('data:')) {
    throw new SecurityError('Data URIs não são permitidos por questões de segurança');
  }

  // Bloquear outros protocolos potencialmente perigosos
  const dangerousProtocols = ['file:', 'ftp:', 'gopher:', 'ldap:', 'dict:'];
  const lowerUrl = url.toLowerCase();

  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      throw new SecurityError(`Protocolo ${protocol} não é permitido por questões de segurança`);
    }
  }

  // Validar formato da URL
  try {
    const urlObj = new URL(url);

    // Permitir apenas HTTP e HTTPS
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      throw new SecurityError(`Protocolo ${urlObj.protocol} não é permitido. Apenas HTTP e HTTPS são aceitos`);
    }

    // Validações adicionais de segurança
    if (urlObj.hostname === 'localhost' || urlObj.hostname === '127.0.0.1' || urlObj.hostname.startsWith('10.') || urlObj.hostname.startsWith('192.168.')) {
      console.warn(`Aviso: URL aponta para rede local/privada: ${url}`);
    }

  } catch (error) {
    if (error instanceof SecurityError) {
      throw error;
    }
    throw new SecurityError(`Formato de URL inválido: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
  }
}

/**
 * Wrapper seguro para configurações do Axios
 * Valida a URL antes de permitir a requisição
 *
 * @param {object} config - Configuração do Axios
 * @returns {object} - Configuração validada
 * @throws {SecurityError} - Se a URL for insegura
 */
export function validateAxiosConfig(config) {
  if (config.url) {
    validateSecureUrl(config.url);
  }

  return config;
}